import { createAsyncThunk } from '@reduxjs/toolkit'
import debounce from 'lodash/debounce'
import geohash from 'ngeohash'
import toast from 'react-hot-toast'
import * as endPoints from '@/endpoints'
import axios from '@/lib/axios'
// import{ easeCubic } from 'd3-ease'

// import * as turf from '@turf/turf'

import * as setUtils from '@/utils/setUtils'
// import {setCircuitFilterButton, setSSIDFilterButton, setFeederFilterButton} from './tab'

// import WebMercatorViewport from "@math.gl/web-mercator"
import {
    deleteAllSourceFeatures,
    filterBaseLayersByCircuitOrSSIDorFeeder,
    geohashesFetched,
    geohashesFetching,
    onChangeViewport,
    selectBuildingsCount,
    selectEHVLineCount,
    selectHVLineCount,
    selectLVLineCount,
    setSearchStatus,
    toggleBoundaryLayerVisibility
} from './map'

// import WebMercatorViewport from "@math.gl/web-mercator";
// /////////////////////////////////////////////////////////////////////////////////////////
// ///////////////// Util Functions ////////////////////////////////
// /////////////////////////////////////////////////////////////////////////////////////////

function getPrecisionFromZoom(zoom) {
    if (zoom >= 10 && zoom < 13) {
        return 4
    }
    if (zoom >= 13) {
        return 5
    }
}

// only used to tell the search result where to zoom to
// export function getZoomFromVoltageLevel(voltageLevel) {
//     if (voltageLevel === 'HV') {
//         return 13.1
//     } if (voltageLevel === 'EHV') {
//         return 15.1
//     } // LV
//     return 17.1
// }

export function getVoltageLevelsFromZoom(zoomLevel) {
    if (zoomLevel >= 4 && zoomLevel < 12) {
        return ['EHV']
    }
    if (zoomLevel >= 12 && zoomLevel < 15) {
        return ['HV', 'EHV']
    }
    if (zoomLevel >= 15 && zoomLevel <= 24) {
        return ['LV', 'HV', 'EHV']
    }
    return []
}

export function getPrecisionFromZoomAndVoltageLevel(
    zoomLevel,
    voltageLevel,
    nodeOrLine
) {
    if (voltageLevel === 'BUILDINGS') {
        if (zoomLevel >= 14) {
            return 5
        }
        return null
    }
    if (voltageLevel === 'LV') {
        if (zoomLevel >= 15) {
            return 6
        }
        return null
    }
    if (voltageLevel === 'HV') {
        if (zoomLevel >= 15) {
            if (nodeOrLine === 'NODE') {
                return 5
            }
            return 6
        }
        if (zoomLevel >= 12 && zoomLevel < 15) {
            return 5
        }
        return null
    }
    if (voltageLevel === 'EHV') {
        if (nodeOrLine === 'NODE') {
            return 3
        }
        if (zoomLevel >= 15) {
            return 6
        }
        if (zoomLevel >= 12 && zoomLevel < 15) {
            return 5
        }
        if (zoomLevel >= 4 && zoomLevel <= 11) {
            return 3
        }
        return null
    }
}

// /////////////////////////////////////////////////////////////////////////////////////////
// ////////////////////////////////////// Thunks ///////////////////////////////////////////
// /////////////////////////////////////////////////////////////////////////////////////////

export const fetchPlanningAreas = createAsyncThunk(
    'map/fetchPlanningAreas',
    async (_, thunkAPI) => {
        const response = await axios.get('')
        return response.data
    }
)

export const fetchPopupFeatures = createAsyncThunk(
    'map/fetchPopupFeatures',
    async (
        { lon, lat, map },
        { getState, fulfillWithValue, rejectWithValue }
    ) => {
        const state = getState()
        const zoomLevel = parseInt(state.map.viewport.zoom, 10)
        const b64Lon = btoa(lon)
        const b64Lat = btoa(lat)
        const voltageLevels = getVoltageLevelsFromZoom(zoomLevel).join('-')

        const response = await axios.get(
            `${''}${b64Lon}/${b64Lat}/${zoomLevel}/${voltageLevels}/`
        )
        return response.data
    }
)

export function fetchSearch({
    searchType,
    searchTerm,
    filterFeaturesAfterTransition,
    map
}) {
    const thunk = (dispatch, getState) => {
        axios
            .get(`${endPoints.SEARCH + searchType}/?search=${searchTerm}`)
            .then((response) => {
                if (response.data.result === 'success') {
                    const state = getState()
                    const { bbox, node } = response.data.results

                    // the bbox returned from the feeder search can be so big that fitBounds moves the map to a level higher than 15
                    // and only shows simplified - detect the intended zoom and ensure we zoom to 15 at most
                    const { longitude, latitude, zoom } =
                        new WebMercatorViewport({
                            width: map.getCanvas().width,
                            height: map.getCanvas().height
                        }).fitBounds(
                            [
                                [bbox[0], bbox[1]],
                                [bbox[2], bbox[3]]
                            ],
                            {
                                padding: 50
                            }
                        )

                    let transitionDuration = 2500
                    // debugger
                    // if ((map.getCenter().lat.toString().slice(0,8) === latitude.toString().slice(0,8)) &&
                    //     (map.getCenter().lng.toString().slice(0,8) === longitude.toString().slice(0,8)) ){
                    //     console.log("we're not moving, so make duration 0")
                    //     transitionDuration=0
                    // }
                    if (
                        map.getCenter().lat.toString().slice(0, 8) ===
                        latitude.toString().slice(0, 8)
                    ) {
                        // console.log("we're not moving, so make duration 0")
                        transitionDuration = 0
                    }

                    // console.log('fuzzy last coord - ',fuzzyCoordLastDigit(bbox[0]))
                    dispatch(deleteAllSourceFeatures())

                    // This has been turned off for all search types
                    // It's a pain to filter something when no feature has been selected
                    // Easier to disable filter buttons if nothing has been selected
                    if (filterFeaturesAfterTransition) {
                        // filter the map features
                        const searchTermNoQuotes =
                            response.data.searchTerm.replace(/"/g, '')

                        dispatch(
                            filterBaseLayersByCircuitOrSSIDorFeeder({
                                currentFilter: state.tab.filterBy,
                                newFilter: response.data.searchType,
                                newId: searchTermNoQuotes
                            })
                        )
                    }
                } else {
                    toast.error('Sorry', response.data.result)
                }
                dispatch(setSearchStatus(false))
            })
            .catch((error) => {
                toast.error('Sorry', 'Search Error')
                dispatch(setSearchStatus(false))
            })
    }
    thunk.meta = {
        debounce: {
            time: 5,
            key: 'fetchSearch',
            leading: false,
            trailing: true
        }
    }
    return thunk
}

export const fetchBoundaries = createAsyncThunk(
    'map/fetchBoundaries',
    async (
        { dnoOrCouncil },
        { dispatch, getState, fulfillWithValue, rejectWithValue }
    ) => {
        try {
            const response = await axios.get(`${''}${dnoOrCouncil}/`)

            if (dnoOrCouncil === 'dno') {
                dispatch(
                    toggleBoundaryLayerVisibility({
                        stateName: 'shouldFetchDNOBoundaries',
                        layerName: 'DNO Boundaries'
                    })
                )
            } else if (dnoOrCouncil === 'council') {
                dispatch(
                    toggleBoundaryLayerVisibility({
                        stateName: 'shouldFetchCouncilBoundaries',
                        layerName: 'Council Boundaries'
                    })
                )
            }

            return fulfillWithValue(
                { visibleBoundaryData: response.data, dnoOrCouncil },
                { 'meta-stuff': 'blah' }
            )
        } catch (rejectedValueOrSerializedError) {
            // console.log('fetching boundaries - failure', rejectedValueOrSerializedError)
        }
    }
)

export const fetchNodesLinesBuildingsByGeohash = ({ map }) => {
    const thunk = async (dispatch, getState) => {
        const state = getState()

        // const point = turf.point([state.map.viewport.longitude, state.map.viewport.latitude]);
        const mapBounds = map.getBounds()
        const mapBoundsBBox = turf.bboxPolygon([
            mapBounds._ne.lng,
            mapBounds._ne.lat,
            mapBounds._sw.lng,
            mapBounds._sw.lat
        ])

        const shouldFetch = state.map.fetchingBoundary[0].filter((poly) =>
            turf.intersect(mapBoundsBBox, turf.polygon([poly]))
        )

        // console.log('should - ', shouldFetch)

        if (!shouldFetch.length) {
            dispatch({
                type: 'map/fetchNodesLinesBuildingsByGeohash/rejected',
                payload: 'Not fetching - Not over the UK'
            })
            console.log('Not fetching - Not over the UK')
            return
        }

        const zoomLevel = Math.floor(state.map.viewport.zoom)

        if (zoomLevel <= 4) {
            // console.log('Too zoomed out - not fetching')
            dispatch({
                type: 'map/fetchNodesLinesBuildingsByGeohash/rejected',
                payload: 'Too zoomed out - not fetching'
            })
            return
        }

        if (
            !state.map.shouldFetchLVNodes &&
            !state.map.shouldFetchLVLines &&
            !state.map.shouldFetchHVNodes &&
            !state.map.shouldFetchHVLines &&
            !state.map.shouldFetchEHVNodes &&
            !state.map.shouldFetchEHVLines &&
            !state.map.shouldFetchBuildings
        ) {
            dispatch({
                type: 'map/fetchNodesLinesBuildingsByGeohash/rejected',
                payload: 'No voltage levels or buildings selected'
            })
            return
        }

        // Ok, we're over the DNO, above zoom level 4, and have some voltages selected
        dispatch({ type: 'map/fetchNodesLinesBuildingsByGeohash/pending' })
        const timeStart = performance.now()

        const bbox = map.getBounds()

        function calculateGeohashes(shouldFetch, voltageLevel, nodeOrLine) {
            if (shouldFetch) {
                const precision = getPrecisionFromZoomAndVoltageLevel(
                    zoomLevel,
                    voltageLevel,
                    nodeOrLine
                )
                // console.log('nodeOrLine, VL, precision - ', nodeOrLine, voltageLevel, precision)
                if (precision) {
                    return geohash.bboxes(
                        bbox._sw.lat,
                        bbox._sw.lng,
                        bbox._ne.lat,
                        bbox._ne.lng,
                        precision
                    )
                }
            }
            return []
        }

        // debugger

        const doesGeoStartWith = (geo, substrs) =>
            substrs.some((substr) => geo.startsWith(substr))
        const allowedGeohashes = (listOfGeohashes) =>
            listOfGeohashes.filter((geo) =>
                doesGeoStartWith(geo, SPENSSE3PrecisionGeohashes)
            )

        const localStorageLineLimits = localStorage.getItem('lineLimits')
        let lineLimits = {}
        if (localStorageLineLimits) {
            lineLimits = JSON.parse(localStorageLineLimits)
        }

        const reduce_geohashes = (geohashes) => {
            const result = {}

            geohashes.forEach((geo_obj) => {
                for (const i in geo_obj.geohashes) {
                    const geohash = geo_obj.geohashes[i]
                    if (!Object.hasOwn(result, geohash)) {
                        // console.log('adding geohash to dict - ', geohash)
                        result[geohash] = []
                    }
                    if (
                        !result[geohash].includes(`${geo_obj.vl}${geo_obj.nol}`)
                    ) {
                        result[geohash].push(`${geo_obj.vl}${geo_obj.nol}`)
                    }
                }
            })
            return result
        }

        const geohash_separator = 'i'

        // now add to 'fetching'
        dispatch(geohashesFetching({}))

        // console.log('Time taken to faff before making fetch:'+ (timeStart-performance.now())/1000 +' seconds')

        const timeStartFeatureFetch = performance.now()

        // try {
        const response = await axios.get(`${'' + ''.join('-')}/`)

        // console.log('Time taken to fetch from backend:'+ (timeStartFeatureFetch-performance.now())/1000 +' seconds')

        dispatch({
            type: 'map/fetchNodesLinesBuildingsByGeohash/fulfilled',
            payload: {
                response: response.data.data
            }
        })

        dispatch(geohashesFetched({}))
    }
    thunk.meta = {
        debounce: {
            time: 500,
            key: 'fetchEventsByGeohash',
            leading: false,
            trailing: true
        }
    }
    return thunk
}
